import { useEffect } from "react";
import { motion } from "framer-motion";

interface ActionCardAlertProps {
  cardType: string;
  attackerName: string;
  isSelfTargeted?: boolean;
  onDismiss: () => void;
}

const CARD_CONFIG: Record<
  string,
  { icon: string; accent: string; label: string; description: string; selfHeadline: string }
> = {
  freeze: {
    icon: "❄",
    accent: "bg-gradient-to-b from-blue-400 to-blue-600",
    label: "FREEZE",
    description: "You've been frozen for this round.",
    selfHeadline: "You froze yourself!",
  },
  flip_three: {
    icon: "🃏",
    accent: "bg-gradient-to-b from-red-400 to-orange-500",
    label: "FLIP THREE",
    description: "3 cards incoming.",
    selfHeadline: "You Flip Three'd yourself!",
  },
  second_chance: {
    icon: "✨",
    accent: "bg-gradient-to-b from-amber-400 to-yellow-500",
    label: "SECOND CHANCE",
    description: "You'll survive one duplicate.",
    selfHeadline: "You gave yourself Second Chance!",
  },
};

function formatHeadline(config: typeof CARD_CONFIG[string], attackerName: string, isSelfTargeted: boolean, isPositive: boolean) {
  if (isSelfTargeted) return config.selfHeadline;
  return `${attackerName} ${isPositive ? "gave you" : "used"} ${config.label}${isPositive ? "!" : " on you!"}`;
}

export default function ActionCardAlert({
  cardType,
  attackerName,
  isSelfTargeted = false,
  onDismiss,
}: ActionCardAlertProps) {
  const config = CARD_CONFIG[cardType] ?? CARD_CONFIG.freeze;
  const isPositive = cardType === "second_chance";

  useEffect(() => {
    const timer = setTimeout(onDismiss, 4500);
    return () => clearTimeout(timer);
  }, [onDismiss]);

  return (
    <motion.div
      key="action-card-alert"
      initial={{ y: -100, opacity: 0, scaleX: 0.9 }}
      animate={{ y: 0, opacity: 1, scaleX: 1 }}
      exit={{ y: -60, opacity: 0 }}
      transition={{
        type: "spring",
        stiffness: 300,
        damping: 22,
        mass: 0.8,
      }}
      className="fixed top-10 left-1/2 -translate-x-1/2 z-40 w-[90vw] max-w-sm"
    >
      <div className="bg-[var(--win2k-button-face)] border-2 win2k-outset flex overflow-hidden">
        <div className={`w-2 shrink-0 ${config.accent}`} />
        <div className="p-2.5 flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="text-base leading-none">{config.icon}</span>
            <span className="text-[12px] font-bold text-[var(--win2k-button-text)] truncate">
              {formatHeadline(config, attackerName, isSelfTargeted, isPositive)}
            </span>
          </div>
          <p className="text-[10px] text-[var(--win2k-shadow)] mt-0.5 ml-6">
            {config.description}
          </p>
        </div>
      </div>
    </motion.div>
  );
}
